/**
 * A visually distinct content card used across admin pages.
 *
 * Wraps Meridian's `Box` in outline mode with generous internal padding.
 * Accepts an optional section heading + optional action cluster on the
 * right of the heading.
 *
 * Example:
 *   <Section title="Recently updated groups" actions={<Button>See all</Button>}>
 *     ...children...
 *   </Section>
 */

import React from 'react';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Heading from '@amzn/meridian/heading';
import Text from '@amzn/meridian/text';

export interface SectionProps {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
  /** Tighter padding when false. Default true. */
  padded?: boolean;
}

export const Section: React.FC<SectionProps> = ({
  title,
  subtitle,
  actions,
  children,
  padded = true,
}) => {
  return (
    <Box type="outline" spacingInset={padded ? '500' : '300'}>
      <Column spacing={padded ? 'medium' : 'small'}>
        {(title || actions) && (
          <Row alignmentHorizontal="justify" alignmentVertical="center" spacing="medium" wrap="down">
            <Column spacing="xsmall" minWidth={0}>
              {title && (
                <Heading level={3} type="h100">
                  {title}
                </Heading>
              )}
              {subtitle && (
                <Text type="b200" color="secondary">
                  {subtitle}
                </Text>
              )}
            </Column>
            {actions && <Row spacing="small">{actions}</Row>}
          </Row>
        )}
        {children}
      </Column>
    </Box>
  );
};
